import { buildAsturHeaders } from '@/endpoints/channel/asturHeaders';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { CommonalityItem } from '@/types/commonality';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../../requestOptions';

export type GlobalCommonalityListData = Readonly<{
  channel_name: string;
  global_set_id: number | null;
  mappings: CommonalityItem[];
  total: number;
  limit: number;
  offset: number;
}>;

type GlobalCommonalityListResponse = Readonly<{
  data: GlobalCommonalityListData;
}>;

export async function getGlobalCommonality(
  scope: RequestScope,
  args: {
    channelName: string;
    commonalityKey?: string;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<GlobalCommonalityListData>> {
  try {
    if (!args.channelName) {
      throw new Error('channelName is required');
    }

    const params: Record<string, string | number> = {
      channel_name: args.channelName,
    };
    if (args.commonalityKey) params.commonality_key = args.commonalityKey;
    if (typeof args.limit === 'number') params.limit = args.limit;
    if (typeof args.offset === 'number') params.offset = args.offset;

    const res = await scopedChannelApi(scope).get<GlobalCommonalityListResponse>('atts/commonality/global', {
      ...options,
      headers: buildAsturHeaders(),
      params,
      signal: args.signal ?? options.signal,
    });

    if (!res.data?.data) {
      throw new Error('Invalid global commonality response');
    }

    return {
      data: res.data.data,
      status: res.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
